/**
 * 浏览器侧环境变量的唯一读取点。
 *
 * 三条不可动摇的规则：
 * 1. 只有这里可以读 `import.meta.env`；其它模块一律 import 本文件的常量，
 *    否则"这个开关到底在哪读的"会在重构中失控。
 * 2. 浏览器只允许读 `VITE_*`。Vite 会在构建期把 `import.meta.env.VITE_XXX`
 *    静态替换成字面量，所以必须写完整的点号访问——`import.meta.env['VITE_XXX']`
 *    不会被替换，运行时是 undefined。
 * 3. 任何密钥（`AI_API_KEY` / `JWT_*_SECRET` / `sk-*`）都不许出现在这里。
 *    CI 的 `scripts/check-secrets.mjs` 把 `apps/web/src/lib/env.ts` 列入必检清单。
 */

/** API 前缀。开发期由 Vite dev server 代理到后端，见 vite.config.ts。 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** 构建期默认是否开启本地 Mock（`VITE_MOCK_MODE=1`）。 */
const MOCK_MODE_DEFAULT: boolean = import.meta.env.VITE_MOCK_MODE === '1';

/** 微信网页登录是否进入"已配置但后端未实现"的说明态。 */
export const WECHAT_WEB_LOGIN_ENABLED: boolean = import.meta.env.VITE_WECHAT_WEB_LOGIN === '1';

/**
 * 是否处于开发构建。
 *
 * `import.meta.env.DEV` 是 Vite 的内置布尔量（构建期被静态替换），不是 `VITE_*`
 * 变量，所以这里读它不违反上面的规则 2；它也不会随 `.env` 文件变化。
 *
 * 用途只有一个：支付页的「模拟支付成功」按钮。开发环境点一下就开通权益，
 * 否则每改一次支付流程都要真的扫码。
 */
export const IS_DEV: boolean = import.meta.env.DEV;

/** 运行期覆盖开关的存储键。 */
const MOCK_STORAGE_KEY = 'spirit.mock';

/**
 * 是否把请求交给本地 Mock。
 *
 * 优先级：URL 查询参数 > localStorage > 构建期默认值。
 * URL 最高，是因为"给别人演示时临时切一下"必须不动构建产物；
 * localStorage 次之，因为它能跨页面保持；构建默认值只是兜底。
 *
 * 读取 localStorage 的异常（隐私模式、站点数据被禁）会被吞掉并退回下一优先级，
 * 否则一个存储异常会让整个应用白屏。
 */
export function isMockEnabled(): boolean {
  const fromQuery = readQueryFlag('mock');
  if (fromQuery !== null) {
    return fromQuery;
  }
  const fromStorage = readStorageFlag();
  if (fromStorage !== null) {
    return fromStorage;
  }
  return MOCK_MODE_DEFAULT;
}

/** 顶部横幅文案；永远返回非空字符串（调用方的 `!== null` 判断因此恒真）。 */
export function mockModeLabel(): string {
  return isMockEnabled() ? '本地 Mock（未连接后端）' : '已连接后端';
}

function readQueryFlag(name: string): boolean | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const raw = new URLSearchParams(window.location.search).get(name);
  if (raw === '1') {
    return true;
  }
  if (raw === '0') {
    return false;
  }
  return null;
}

function readStorageFlag(): boolean | null {
  try {
    const raw = window.localStorage.getItem(MOCK_STORAGE_KEY);
    if (raw === '1') {
      return true;
    }
    if (raw === '0') {
      return false;
    }
    return null;
  } catch {
    return null;
  }
}
